import {
  addStepProgress,
  addStepProgressList,
} from '@/store/reducers/StepProgressStore';
import { cacheReportForThread } from '@/store/reducers/ReportWidgetStore';
import { fetchThreadState } from '@/store/thunks/FetchThreads';
import { getCurrentUser } from '@/store/reducers/UserStore';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { getReportWidgetDataFromApi } from '@/models/Report';
import {
  getStepProgressFromGraphQL,
  GQL_STEP_PROGRESS_FRAGMENT,
  GQL_STEP_PROGRESS_FRAGMENT_NAME,
} from '@/models/StepProgress';
import { isPojo } from '@/utils/typeUtils';
import { mkUserQueryConsentKey } from '@/constants/localStorage';
import { openPaperFinderWidget } from '@/store/actionCreators/WidgetActions';
import {
  processDvWidgetPubsubEvent,
  processTableWidgetPubsubEvent,
} from '@/components/chat/chatThreadHooksUtil';
import { setActiveWidgetDataForThread } from '@/store/reducers/PaperFinderWidgetStore';
import { ThreadId } from '@/models/Thread';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useTraces } from '@/hooks/traceHooks';
import { WidgetType } from '@/models/PanelWidgets';

import { gql, useQuery, useSubscription } from '@apollo/client';
import { useEffect } from 'react';

const ON_NEW_MESSAGE_SUBSCRIPTION = gql`
  subscription ChatOnNewMessage($threadId: ID!) {
    onNewMessage(threadId: $threadId) {
      id
      sender {
        uuid
      }
      thread {
        id
      }
    }
  }
`;

// Listen for new messages on a thread, then fetches all thread messages
export function useReloadOnNewMessage(threadId: ThreadId | null): {
  error: any;
} {
  const dispatch = useAppDispatch();
  const isReady = threadId !== null;
  const { traceBotResponseMessage } = useTraces();

  const { data, error } = useSubscription(ON_NEW_MESSAGE_SUBSCRIPTION, {
    skip: !isReady,
    variables: {
      threadId,
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    const msg = data.onNewMessage;
    const msgThreadId = msg?.thread?.id ?? threadId;
    if (!msgThreadId) {
      return;
    }
    dispatch(fetchThreadState(msgThreadId));

    // Trace timing of when agent response is received and displayed to user
    traceBotResponseMessage({
      threadId: msgThreadId,
      senderId: msg.sender.uuid,
    });
  }, [data]);

  return { error };
}

const ON_UPDATE_WIDGET_SUBSCRIPTION = gql`
  subscription ChatOnUpdateWidget($threadId: ID!) {
    onUpdateWidget(threadId: $threadId) {
      id
      thread {
        id
      }
      type
      dataJSON
    }
  }
`;

// Listen for updates to a widget and reload the widget
export function useReloadOnUpdateWidget(threadId: ThreadId | null): {
  error: any;
} {
  const dispatch = useAppDispatch();
  const isReady = !!threadId;

  const { data, error } = useSubscription(ON_UPDATE_WIDGET_SUBSCRIPTION, {
    skip: !isReady,
    variables: {
      threadId,
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    const msg = data.onUpdateWidget;
    const widgetId = msg?.id;
    const threadId = msg?.thread?.id;
    const widgetType = msg?.type;
    const widgetData = msg?.dataJSON;
    if (!widgetId || !threadId || !widgetData) {
      return;
    }

    switch (widgetType) {
      case 'WIDGET_TABLE': {
        processTableWidgetPubsubEvent({
          threadId,
          widgetDataStr: widgetData,
          dispatch,
        });
        break;
      }
      case 'WIDGET_DATA_VOYAGER': {
        processDvWidgetPubsubEvent({
          threadId,
          widgetDataStr: widgetData,
          dispatch,
        });
        break;
      }
    }
  }, [data]);

  return { error };
}

const ON_NEW_WIDGET_SUBSCRIPTION = gql`
  subscription ChatOnNewWidget($threadId: ID!) {
    onNewWidget(threadId: $threadId) {
      id
      thread {
        id
      }
      type
      dataJSON
    }
  }
`;

// Listen for new widgets on a thread and fetch the widget data from the API
export function useReloadOnNewWidget(threadId: ThreadId | null): {
  error: any;
} {
  const dispatch = useAppDispatch();
  const isReady = !!threadId;
  const { traceWidgetCreated } = useTraces();

  const { data, error } = useSubscription(ON_NEW_WIDGET_SUBSCRIPTION, {
    skip: !isReady,
    variables: {
      threadId,
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    const msg = data.onNewWidget;
    const widgetId = msg?.id;
    const threadId = msg?.thread?.id;
    const widgetType = msg?.type;
    const widgetData = msg?.dataJSON;
    if (!widgetId || !threadId || !widgetData) {
      return;
    }

    switch (widgetType) {
      case 'WIDGET_REPORT': {
        const reportDataJSON = JSON.parse(widgetData);
        const reportData = getReportWidgetDataFromApi(reportDataJSON);
        if (!isPojo(reportData)) {
          return;
        }
        dispatch(
          cacheReportForThread({
            threadId,
            reportData: {
              type: WidgetType.REPORT,
              id: reportData.id,
              sections: reportData.sections,
            },
          }),
        );
        break;
      }
      case 'WIDGET_PAPER_FINDER': {
        const data = JSON.parse(widgetData);
        dispatch(setActiveWidgetDataForThread({ threadId, data }));
        dispatch(openPaperFinderWidget({ threadId, widgetId }));
        break;
      }
      case 'WIDGET_DATA_VOYAGER': {
        processDvWidgetPubsubEvent({
          threadId,
          widgetDataStr: widgetData,
          dispatch,
        });
        break;
      }
    }

    // Trace timing of when a widget is created and displayed to user
    traceWidgetCreated({ threadId, widgetId, widgetType });
  }, [data]);

  return { error };
}

export const FETCH_THREAD_STEP_PROGRESS = gql`
  ${GQL_STEP_PROGRESS_FRAGMENT}

  query ChatFetchThreadStepProgress($threadId: ID!) {
    thread(id: $threadId) {
      id
      latestStepProgress {
        ...${GQL_STEP_PROGRESS_FRAGMENT_NAME}
      }
      messages {
        edges {
          node {
            id
            latestStepProgress {
              ...${GQL_STEP_PROGRESS_FRAGMENT_NAME}
            }
          }
        }
      }
    }
  }
`;

export function useInitializeStepProgress(threadId: ThreadId | null): {
  error: any;
} {
  const dispatch = useAppDispatch();

  const { data, error } = useQuery(FETCH_THREAD_STEP_PROGRESS, {
    skip: !threadId,
    variables: {
      threadId,
    },
  });

  useEffect(() => {
    const thread = data?.thread;
    if (!thread) {
      return;
    }

    const threadStepProgress = thread.latestStepProgress.map(
      (stepProgress: any) => getStepProgressFromGraphQL(stepProgress),
    );
    const messageStepProgress = thread.messages.edges
      .flatMap(({ node }: any) => node.latestStepProgress)
      .filter(Boolean)
      .map((stepProgress: any) => getStepProgressFromGraphQL(stepProgress));
    const allStepProgress = [...threadStepProgress, ...messageStepProgress];
    console.log({
      thread,
      threadStepProgress,
      messageStepProgress,
      allStepProgress,
    });
    console.log('GQL: FetchThreadStepProgress', {
      threadId,
      threadStepProgress,
      messageStepProgress,
    });
    dispatch(addStepProgressList(allStepProgress));
  }, [data]);

  return { error };
}

const ON_UPDATE_THREAD_STEP_PROGRESS = gql`
  ${GQL_STEP_PROGRESS_FRAGMENT}

  subscription ChatOnUpdateThreadStepProgress($threadId: ID!) {
    onUpdateThreadStepProgress(threadId: $threadId) {
      ...${GQL_STEP_PROGRESS_FRAGMENT_NAME}
      event {
        id
        type
        dataJSON
      }
    }
  }
`;

// Listen for updates to the thread's step progress and update the store
export function useUpdateStoreOnStepProgress(threadId: ThreadId | null): {
  error: any;
} {
  const dispatch = useAppDispatch();
  const isReady = !!threadId;
  const { traceProcessingStepProgress } = useTraces();

  const { error } = useSubscription(ON_UPDATE_THREAD_STEP_PROGRESS, {
    skip: !isReady,
    variables: {
      threadId,
    },
    onData: (subscriptionData) => {
      const data = subscriptionData?.data?.data;
      if (!data) {
        return;
      }
      const rawStepProgress = data?.onUpdateThreadStepProgress;
      const stepProgress = rawStepProgress
        ? getStepProgressFromGraphQL(rawStepProgress)
        : null;

      if (stepProgress) {
        dispatch(addStepProgress(stepProgress));

        // Trace timing of when the first processing step is recieved and displayed to user
        traceProcessingStepProgress(stepProgress);
      }
    },
    onError: (error) => {
      getLayoverLogger().logError('Error in subscription:', error);
    },
  });

  return { error };
}

export const useShouldShowQueryConsentModal = (): boolean => {
  const currentUser = useAppSelector(getCurrentUser);
  if (!currentUser) {
    getLayoverLogger().logError('No current user found for consent modal');
    return false;
  }

  const userId = currentUser.uuid;
  const existingConsent = localStorage.getItem(mkUserQueryConsentKey(userId));

  if (existingConsent) {
    return false;
  }

  return true;
};
